import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import TemplateMenubar from "./components/template-menubar";
import { InputText } from "primereact/inputtext";
import { InputIcon } from "primereact/inputicon";
import './template.css';
import TemplateCard from "./components/template-card";
import { useEffect, useState, useRef } from "react";
import { useGetTemplatesQuery } from "src/redux/survey/template-endpoints";
import { SurveyTemplate } from "src/redux/survey/template.types";

const TemplateScreen = () => {
  useSetPageTitle('Generate Survey');
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState<Record<string, SurveyTemplate[]>>();
  const [activeSection, setActiveSection] = useState<string>('');
  const { data } = useGetTemplatesQuery(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const isInitialMount = useRef(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isManualScrolling = useRef(false);
  const manualScrollTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (data) {
      setTemplates(data);
      const firstCategory = Object.keys(data).find(category => category !== "DEFAULT");
      if (firstCategory && isInitialMount.current) {
        setActiveSection(firstCategory);
        isInitialMount.current = false;
      }
    }
  }, [data]);

  // Auto-select sections while scrolling
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-20% 0px -80% 0px', // Triggers when section is 20% from the top
      threshold: 0,
    };
    const callback: IntersectionObserverCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isManualScrolling.current) {
          const sectionId = entry.target.id;
          setActiveSection(sectionId);
        }
      });
    };
    observerRef.current = new IntersectionObserver(callback, options);
    Object.values(sectionRefs.current).forEach(section => {
      if (section) {
        observerRef.current?.observe(section);
      }
    });
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (manualScrollTimeout.current) {
        clearTimeout(manualScrollTimeout.current);
      }
    };
  }, [templates]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = e.target.value.toLowerCase();
    setSearch(searchText);

    if (data) {
      const filteredTemplates = Object.entries(data).reduce<Record<string, SurveyTemplate[]>>(
        (acc, [category, templates]) => {
          const matchingTemplates = templates.filter(template =>
            template.name.toLowerCase().includes(searchText) ||
            template.summary.toLowerCase().includes(searchText)
          );

          if (matchingTemplates.length > 0) {
            acc[category] = matchingTemplates;
          }

          return acc;
        },
        {}
      );

      setTemplates(filteredTemplates);

      // Always select the first visible category after search
      const categories = Object.keys(filteredTemplates).filter(category => category !== "DEFAULT");
      if (categories.length > 0) {
        setActiveSection(categories[0]);
      }
    }
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = sectionRefs.current[sectionId];
    if (element) {
      isManualScrolling.current = true;
      element.scrollIntoView({ behavior: 'smooth' });
      if (manualScrollTimeout.current) {
        clearTimeout(manualScrollTimeout.current);
      }
      manualScrollTimeout.current = setTimeout(() => {
        isManualScrolling.current = false;
      }, 1000);
    }
  };

  return (
    <div className="h-full">
      <TemplateMenubar />
      <div className="template-container">
        <div className="search-container mobile-search">
          <div className="relative w-full">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <InputIcon className="pi pi-search text-gray-400" />
            </span>
            <InputText
              value={search}
              placeholder="Search"
              className="w-full rounded-lg border border-gray-300 p-2 pl-10 shadow-md"
              onChange={handleSearch}
            />
          </div>
        </div>

        <aside className="template-sidebar">
          <div className="search-container desktop-search">
            <div className="relative w-full">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <InputIcon className="pi pi-search text-gray-400" />
              </span>
              <InputText
                value={search}
                placeholder="Search"
                className="w-full rounded-lg border border-gray-300 p-2 pl-10 shadow-md"
                onChange={handleSearch}
              />
            </div>
          </div>

          {templates && Object.keys(templates)
            .filter(category => category !== "DEFAULT")
            .map(category => (
              <a
                key={category}
                href={`#${category}`}
                className={`toc-link ${activeSection === category ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(category);
                }}
              >
                {category}
              </a>
            ))}
        </aside>

        <main className="template-content">
          <h1 className="text-3xl font-light mb-5">
            Agent library
          </h1>

          {templates && Object.entries(templates)
            .filter(([category]) => category !== "DEFAULT")
            .map(([category, categoryTemplates]) => (
              <section
                key={category}
                id={category}
                ref={el => {
                  sectionRefs.current[category] = el;
                  if (el && observerRef.current) {
                    observerRef.current.observe(el);
                  }
                }}
                className="template-section"
              >
                <h2 className="template-section-title">
                  {category}
                </h2>
                <div className="template-grid">
                  {categoryTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      id={template.id}
                      title={template.name}
                      description={template.summary}
                      icon={template.icon}
                    />
                  ))}
                </div>
              </section>
            ))}

          {(!templates || Object.keys(templates).length === 0) && (
            <div className="text-gray-500 mt-8">
              No templates found.
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TemplateScreen;
